import { Button as AntButton } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import type { ButtonProps as AntButtonProps } from "antd";
import type { ButtonHTMLAttributes } from "react";
import { cn } from "@/shared/lib/utils";

type ButtonVariant = "default" | "outline" | "ghost" | "destructive" | "secondary" | "link";
type ButtonSize = "default" | "sm" | "lg" | "icon" | "icon-sm" | "icon-lg";

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "color">,
    Pick<AntButtonProps, "loading" | "htmlType"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  danger?: boolean;
  /** In-flight action (same as `loading`): the platform's one loading look,
   *  a spinner in the middle of the button over its label, which stays
   *  visible (same size), the button faded under a light veil like a
   *  disabled one, and no clicks until it's done. */
  busy?: boolean;
  /** "start" / "end": the spinner fades in just left / right of the label
   *  without moving it (sign-in / sign-up screens only; "end" for an inline
   *  link with text before it, e.g. "Didn't get the code? Resend OTP").
   *  Everything else is "center". */
  spinner?: "center" | "start" | "end";
}

// Maps the old custom variant system onto AntD's type/danger/ghost API.
// AntD only has 5 native "type"s (primary/default/dashed/link/text), so
// outline/secondary/ghost are all built on "default" + className overrides
// to preserve the exact old hover behavior (border+text flip to brand red).
const VARIANT_TO_ANTD: Record<ButtonVariant, { type: AntButtonProps["type"]; extraClass: string }> = {
  default: { type: "primary", extraClass: "" },
  destructive: { type: "primary", extraClass: "" }, // colorError === colorPrimary in theme, so identical render
  outline: { type: "default", extraClass: "msq-btn-outline" },
  secondary: { type: "default", extraClass: "msq-btn-outline" }, // visually identical to outline in the original
  ghost: { type: "text", extraClass: "msq-btn-ghost" },
  link: { type: "link", extraClass: "" },
};

const SIZE_TO_ANTD: Record<ButtonSize, AntButtonProps["size"]> = {
  default: "middle",
  sm: "small",
  lg: "large",
  icon: "middle",
  "icon-sm": "small",
  "icon-lg": "large",
};

const ICON_ONLY_SIZE_CLASS: Partial<Record<ButtonSize, string>> = {
  icon: "!h-8 !w-8 !rounded-full !p-0",
  "icon-sm": "!h-6 !w-6 !rounded-full !p-0",
  "icon-lg": "!h-10 !w-10 !rounded-full !p-0",
};

export function Button({
  variant = "default",
  size = "default",
  danger = false,
  className,
  children,
  busy = false,
  loading,
  spinner = "center",
  onClick,
  ...props
}: ButtonProps) {
  const { type, extraClass } = VARIANT_TO_ANTD[variant];
  const inFlight = busy || Boolean(loading);
  const isIconOnly = size.startsWith("icon");
  const centered = spinner === "center";

  return (
    <AntButton
      type={type}
      danger={danger}
      size={SIZE_TO_ANTD[size]}
      shape={isIconOnly ? "circle" : "default"}
      className={cn(
        // Anchors the spinner and the busy veil (a caller's own position class still wins).
        "relative",
        extraClass,
        isIconOnly && ICON_ONLY_SIZE_CLASS[size],
        className,
        // No hover look and no second click while it runs.
        inFlight && "!pointer-events-none",
      )}
      aria-busy={inFlight || undefined}
      {...props}
      // While in flight it's "loading", not "disabled": it keeps its own
      // colours (antd only dims it) instead of switching to the disabled look.
      disabled={inFlight ? false : props.disabled}
      // Our own spinners (below), never antd's: its loading icon pushes the label.
      loading={false}
      // While busy: no click, and no form submit from a submit button either.
      onClick={inFlight ? (event) => event.preventDefault() : onClick}
    >
      {/* Busy: a light white veil fades the button's colour and label (the
          same faded look as a disabled button), while the spinner stays on
          top of it at full strength. */}
      {inFlight && (
        <span aria-hidden="true" className="pointer-events-none absolute -inset-px z-[1] rounded-[inherit] bg-white/45" />
      )}
      {centered ? (
        children
      ) : (
        // "start" / "end": the label stays put; the spinner floats in beside it.
        <span className="relative inline-flex items-center">
          <span
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute z-[2] flex items-center transition-opacity duration-200 ease-out [filter:drop-shadow(0_0_1.5px_rgba(10,41,71,0.55))]",
              spinner === "end" ? "left-full ml-1.5" : "right-full mr-1.5",
              inFlight ? "opacity-100" : "opacity-0",
            )}
          >
            {inFlight && <LoadingOutlined spin />}
          </span>
          {children}
        </span>
      )}
      {/* Centred spinner over the label: absolute, so the button keeps its
          exact size and text; full label colour and taller than the
          text so it's clearly visible that something is running. */}
      {centered && inFlight && (
        <span aria-hidden="true" className="pointer-events-none absolute inset-0 z-[2] flex items-center justify-center text-[24px] [filter:drop-shadow(0_0_1.5px_rgba(10,41,71,0.55))]">
          <LoadingOutlined spin />
        </span>
      )}
    </AntButton>
  );
}
